import { Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { TenantSummary } from '@yawi/types';

interface RecentTenantsTableProps {
  tenants: TenantSummary[];
  isLoading?: boolean;
}

const planColors: Record<string, string> = {
  free: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  starter: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  professional: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  enterprise: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

const statusColors: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  suspended: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

export function RecentTenantsTable({ tenants, isLoading }: RecentTenantsTableProps) {
  if (isLoading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Recent Tenants
          </h3>
        </div>
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
          Recent Tenants
        </h3>
        <Link
          to="/tenants"
          className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400"
        >
          View All
        </Link>
      </div>

      <div className="space-y-4">
        {tenants.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            No tenants yet
          </p>
        ) : (
          tenants.map((tenant) => (
            <Link
              key={tenant.id}
              to={`/tenants/${tenant.id}`}
              className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-800 dark:text-white/90">
                    {tenant.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {tenant.slug}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    planColors[tenant.plan] || planColors.free
                  }`}
                >
                  {tenant.plan}
                </span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    statusColors[tenant.status] || statusColors.pending
                  }`}
                >
                  {tenant.status}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ${tenant.mrr.toLocaleString()}/mo
                </span>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
